/**
 * 사이드바.
 *
 * 접기 상태를 기억한다 — 노트북에서 표를 넓게 보려고 접은 사람이 새로고침마다
 * 다시 접어야 하면 금방 짜증이 난다. 저장이 막힌 환경(사생활 보호 창 등)에서도
 * 화면은 그대로 떠야 하므로 읽기·쓰기를 모두 감싼다.
 *
 * **좁은 화면(1024px 미만)에서는 서랍이다.** 평소에는 화면 밖에 있다가 상단 바의 메뉴
 * 단추로 연다. 휴대폰에서 256px 를 늘 차지하면 본문에 100px 남짓만 남는다. 서랍에서는
 * 접힘을 따르지 않는다 — 열었는데 글자 없는 아이콘만 있으면 무엇을 누를지 모른다.
 */
import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronRight, MonitorPlay, X } from 'lucide-react';
import { DOCS_NAV, MANUAL_NAV, SYSTEM_ADMIN_NAV, visibleSections, visibleSystemAdmin } from '../../nav.js';
import { visibleDocs } from '../../docs/registry.js';

const STORAGE_KEY = 'signageone.sidebar.collapsed';

/** 문서 목록 한 줄. 지금 읽는 문서가 어느 것인지 목록 안에서 드러나야 한다. */
const docLinkClass = ({ isActive }: { isActive: boolean }): string =>
  `block truncate rounded-md py-1.5 pl-4 pr-2 text-[12.5px] transition-colors ${
    isActive ? 'bg-white/[0.06] font-medium text-white' : 'text-slate-500 hover:bg-white/[0.04] hover:text-slate-300'
  }`;

/** 정비 모드 하위 한 줄. 위 묶음보다 한 단계 작게 — 머리 줄 아래에 매인 것임이 드러나야 한다. */
const subItemClass = (active: boolean, collapsed: boolean): string =>
  `flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium transition-all duration-150 ${
    collapsed ? 'justify-center' : ''
  } ${active ? 'bg-th-5/15 text-white' : 'text-slate-400 hover:bg-white/[0.04] hover:text-slate-200'}`;

export function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeCollapsed(value: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, value ? '1' : '0');
  } catch {
    // 저장이 막혀도 이번 세션 동안은 동작한다.
  }
}

export function Sidebar({
  collapsed,
  onToggle,
  drawer,
  open,
  onClose,
  tenantLabel,
  capabilities,
}: {
  /** 아이콘만 남긴다. 넓은 화면에서만 참이다. */
  collapsed: boolean;
  onToggle: () => void;
  /** 서랍으로 쓰는가(좁은 화면). */
  drawer: boolean;
  /** 서랍이 열려 있는가. */
  open: boolean;
  onClose: () => void;
  tenantLabel: string;
  capabilities: readonly string[];
}) {
  const sections = visibleSections(capabilities);
  const docs = visibleDocs(capabilities);
  const systemItems = visibleSystemAdmin(capabilities);
  const location = useLocation();
  const inDocs = location.pathname === DOCS_NAV.path || location.pathname.startsWith(`${DOCS_NAV.path}/`);
  const inSystem = systemItems.some(
    (item) => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`),
  );

  /*
   * 그 안의 화면을 보고 있으면 펼친 채로 시작한다.
   *
   * 어느 메뉴·문서를 보는 중인지 옆에 보여야 다음 것으로 건너뛴다. 매번 접힌 채로 열리면
   * 옮길 때마다 같은 곳을 두 번 눌러야 한다.
   */
  const [systemOpen, setSystemOpen] = useState(inSystem);
  const [docsOpen, setDocsOpen] = useState(inDocs);

  /*
   * 펼친 묶음을 스크롤 안으로 끌어온다.
   *
   * 정비 모드는 스크롤 영역의 맨 끝이라, 낮은 화면에서는 펼쳐도 하위 항목이 화면 밖에
   * 생긴다. 누른 사람은 아무 일도 안 일어난 줄 안다. 목록이 자라는 두 경우(묶음·문서 목록을
   * 펼칠 때)에 따라간다 — 처음 그릴 때 펼쳐져 있으면 지금 보는 메뉴가 거기 있다는 뜻이다.
   */
  const systemRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (systemOpen) systemRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [systemOpen, docsOpen]);

  return (
    <>
      {/* 서랍 뒤를 덮는다 — 누르면 닫힌다. 본문을 만지다 메뉴가 눌리는 일을 막는다. */}
      {drawer && open ? (
        <div className="fixed inset-0 z-[35] bg-slate-900/40 lg:hidden" aria-hidden="true" onClick={onClose} />
      ) : null}

      <aside
        className={`h-viewport fixed left-0 top-0 z-40 flex w-64 flex-col transition-all duration-300 ease-in-out lg:translate-x-0 ${
          open ? 'translate-x-0 shadow-2xl' : '-translate-x-full'
        } ${collapsed ? 'lg:w-20' : 'lg:w-64'}`}
        style={{ background: 'linear-gradient(180deg, rgb(var(--th-3)) 0%, rgb(var(--th-3) / 0.95) 100%)' }}
        /* 닫힌 서랍은 화면 밖에 있어도 탭으로 들어갈 수 있다. 닫혀 있는 동안은 없는 것으로 둔다. */
        inert={drawer && !open ? true : undefined}
        aria-label="주 메뉴"
      >
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-32"
          style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.03) 0%, transparent 100%)' }}
        />

        <div className="relative flex h-16 items-center gap-3 border-b border-white/[0.06] px-5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-th-5 shadow-lg shadow-th-5/20">
            <MonitorPlay className="h-[18px] w-[18px] text-[#1a2e05]" strokeWidth={2.2} />
          </div>
          {!collapsed && (
            <div className="min-w-0 overflow-hidden">
              <h1 className="text-[15px] font-semibold tracking-tight text-white">SignageOne</h1>
              <p className="truncate text-[11.5px] font-medium tracking-wide text-slate-400">{tenantLabel}</p>
            </div>
          )}
          {drawer ? (
            <button
              type="button"
              onClick={onClose}
              aria-label="메뉴 닫기"
              className="-mr-2 ml-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-white/[0.06] hover:text-slate-200"
            >
              <X className="h-5 w-5" strokeWidth={2} />
            </button>
          ) : null}
        </div>

        <nav className="relative flex-1 overflow-y-auto overflow-x-hidden px-3 py-4">
          {sections.map((section) => (
            <div key={section.title} className="mb-6">
              {collapsed ? (
                <div className="mb-2 h-px bg-white/[0.06]" />
              ) : (
                <p className="mb-2 px-3 text-[11.5px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                  {section.title}
                </p>
              )}
              <ul className="space-y-0.5">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.path}>
                      <NavLink
                        to={item.path}
                        end={item.path === '/'}
                        title={collapsed ? item.label : undefined}
                        className={({ isActive }) =>
                          `group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-[13.5px] font-medium transition-all duration-150 ${
                            collapsed ? 'justify-center' : ''
                          } ${
                            isActive
                              ? 'bg-th-5/15 text-white'
                              : 'text-slate-400 hover:bg-white/[0.04] hover:text-slate-200'
                          }`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            {/* 활성 표시는 왼쪽 막대로 — 접힌 상태에서도 보인다. */}
                            {isActive && (
                              <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r bg-th-5" />
                            )}
                            <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.9} />
                            {!collapsed && <span className="truncate">{item.label}</span>}
                          </>
                        )}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          {/*
            정비 모드 — **관리자에게만 낸다.** 서버 설치 파일 · 출하 검사 · API 정의 ·
            시스템 문서 · 에이전트를 하위로 품는다(`nav.ts` 의 `SYSTEM_ADMIN_NAV`).

            하단 고정 자리가 아니라 스크롤 영역의 맨 끝에 둔다. 하단에 두면 펼칠 때 위로 자라며
            위쪽 메뉴를 밀어내고, 하위 항목이 바로 위의 사용 설명서에 매인 것처럼 보였다.
            머리 줄은 링크가 아니라 여닫는 손잡이다.
          */}
          {systemItems.length > 0 ? (
            <div ref={systemRef} className="mb-6">
              {collapsed ? <div className="mb-2 h-px bg-white/[0.06]" /> : null}
              <button
                type="button"
                onClick={() => setSystemOpen((open) => !open)}
                aria-expanded={systemOpen}
                aria-controls="system-admin-menu"
                title={collapsed ? SYSTEM_ADMIN_NAV.label : undefined}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-[13.5px] font-medium transition-all duration-150 ${
                  collapsed ? 'justify-center' : ''
                } ${inSystem && !systemOpen ? 'bg-th-5/15 text-white' : 'text-slate-400 hover:bg-white/[0.04] hover:text-slate-200'}`}
              >
                <SYSTEM_ADMIN_NAV.icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.9} />
                {!collapsed && (
                  <>
                    <span className="flex-1 truncate text-left">{SYSTEM_ADMIN_NAV.label}</span>
                    <ChevronDown
                      className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${systemOpen ? '' : '-rotate-90'}`}
                      strokeWidth={2.2}
                    />
                  </>
                )}
              </button>

              {systemOpen && (
                <ul id="system-admin-menu" className={`mt-0.5 space-y-0.5 ${collapsed ? '' : 'ml-[21px] border-l border-white/[0.06] pl-2'}`}>
                  {systemItems.map((item) => {
                    const Icon = item.icon;
                    const isDocs = item.path === DOCS_NAV.path;
                    return (
                      <li key={item.path}>
                        {isDocs && !collapsed ? (
                          <>
                            {/*
                              시스템 문서도 링크가 아니라 여닫는 손잡이다. 눌러서 목록을 보고 그 안에서
                              고른다 — 부모와 첫 자식이 같은 곳으로 가면 같은 곳을 두 번 만들어 둔 셈이 된다.
                            */}
                            <button
                              type="button"
                              onClick={() => setDocsOpen((open) => !open)}
                              aria-expanded={docsOpen}
                              className={`${subItemClass(inDocs && !docsOpen, false)} w-full`}
                            >
                              <Icon className="h-4 w-4 shrink-0" strokeWidth={1.9} />
                              <span className="flex-1 truncate text-left">{item.label}</span>
                              <ChevronDown
                                className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${docsOpen ? '' : '-rotate-90'}`}
                                strokeWidth={2.2}
                              />
                            </button>
                            {/*
                              첫 항목은 소개(랜딩)다. 문서를 처음 여는 사람은 어느 문서부터 봐야 할지
                              모른다 — 목록 맨 위에 "여기부터" 가 있어야 한다.
                            */}
                            {docsOpen && (
                              <ul className="mt-0.5 space-y-0.5 pl-5">
                                <li>
                                  <NavLink to={DOCS_NAV.path} end className={docLinkClass}>
                                    소개
                                  </NavLink>
                                </li>
                                {docs.map((doc) => (
                                  <li key={doc.slug}>
                                    <NavLink to={`/docs/${doc.slug}`} className={docLinkClass}>
                                      {doc.title}
                                    </NavLink>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </>
                        ) : (
                          <NavLink
                            to={item.path}
                            // 하위 화면(서버 설치 파일 ▸ 설치 매뉴얼 등)에서도 그 메뉴가 켜져 있게 앞부분으로 맞춘다.
                            end={false}
                            title={collapsed ? item.label : undefined}
                            className={({ isActive }) => subItemClass(isActive || (isDocs && inDocs), collapsed)}
                          >
                            <Icon className="h-4 w-4 shrink-0" strokeWidth={1.9} />
                            {!collapsed && <span className="truncate">{item.label}</span>}
                          </NavLink>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ) : null}
        </nav>

        {/*
          사용 설명서 — 하단 고정.

          스크롤 영역 밖에 두는 것이 요점이다. 위쪽 메뉴가 길어져도 이것은 늘 같은 자리에
          있고, 위를 훑다가 우연히 발견하는 것이 아니라 **찾으면 거기 있는** 메뉴가 된다.
          누구에게나 보이는 메뉴라 권한에 따라 모양이 바뀌지 않는다.
        */}
        <div className="relative border-t border-white/[0.06] px-3 pb-3 pt-3">
          <NavLink
            to={MANUAL_NAV.path}
            title={collapsed ? MANUAL_NAV.label : undefined}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-[13.5px] font-medium transition-all duration-150 ${
                collapsed ? 'justify-center' : ''
              } ${isActive ? 'bg-th-5/15 text-white' : 'text-slate-400 hover:bg-white/[0.04] hover:text-slate-200'}`
            }
          >
            <MANUAL_NAV.icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.9} />
            {!collapsed && <span className="truncate">{MANUAL_NAV.label}</span>}
          </NavLink>

        </div>

        {/* 접기는 넓은 화면의 것이다. 서랍은 닫기 단추가 대신한다. */}
        {drawer ? null : (
          <button
            type="button"
            onClick={onToggle}
            aria-label={collapsed ? '사이드바 펼치기' : '사이드바 접기'}
            className="relative flex h-12 items-center justify-center border-t border-white/[0.06] text-slate-500 transition-colors hover:bg-white/[0.04] hover:text-slate-300"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        )}
      </aside>
    </>
  );
}
